import { _getLogger } from '@doxyme/analytics';
import {
  Specs,
  type BasicObservable,
  type Message,
} from '@doxyme/extensions-common';
import type Instance from '../../../instance';
import { type IframeRuntime } from '../../../runtimes/iframeRuntime';
import type NotificationCapability from '../../notification';
import AppletCapability from '../applet';
import NotificationIcon = Specs.Notification.NotificationIcon;

type Constraints = Specs.CallFrameApplet.Constraints;
type Applet = Specs.CallFrameApplet.Applet;
type AppletPayload = Specs.Applet.AppletPayload;
type AppletConfig = Specs.CallFrameApplet.Config;
const DEFAULT_LAYOUT = Specs.CallFrameApplet.DEFAULT_APPLET_LAYOUT;

const { CallFrameApplet } = Specs;

const logger = _getLogger('callFrameApplet', {
  component: 'extensions',
  lib: 'bridge',
});

/**
 * For use in a call, Call Frame Applet exposes the applets registered to any extension using this capability.
 */
export default class CallFrameAppletCapability extends AppletCapability<Constraints> {
  static id = CallFrameApplet.id;

  constructor() {
    super(CallFrameApplet);
    this.handlers.push(
      [CallFrameApplet.Actions.Attach, this.attach.bind(this)],
      [CallFrameApplet.Actions.Detach, this.detach.bind(this)],
      [CallFrameApplet.Actions.UpdateHeader, this.updateHeader.bind(this)],
    );
  }

  override addInstance(instance: Instance): void {
    super.addInstanceBase(instance);

    const showers = this.configsBy(instance).filter((c) => c.attach);

    if (this._frames.value.length > 0) {
      this.enforceSingleFrame(instance, this._frames.value[0] as Applet);
      return;
    }

    this.attachRuntimes(instance, showers.splice(0, 1));
  }

  override attach(
    configName: AppletPayload | undefined,
    instance: Instance,
  ): void {
    if (this._frames.value.length > 0) {
      this.enforceSingleFrame(instance, this._frames.value[0] as Applet);
      return;
    }

    this.attachOne(configName, instance);
  }

  updateHeader(
    headerUpdate: Partial<Applet['header']>,
    instance: Instance,
  ): void {
    const currentApplet = this._frames.value[0];
    if (!currentApplet) {
      return;
    }
    const runtime = this.runtimes[currentApplet.runtimeId];
    if (!runtime) {
      return;
    }
    if (runtime.instance.id !== instance.id) {
      logger.error(
        'Invalid instance for update header',
        this.getLoggerProps(instance, currentApplet as Applet),
      );
      return;
    }

    if (
      !headerUpdate ||
      !Specs.CallFrameApplet.isValidHeaderConfig(headerUpdate)
    ) {
      logger.error(
        'Invalid header update config',
        this.getLoggerProps(instance, currentApplet as Applet),
      );
      return;
    }

    this._frames.value = (this._frames.value as Applet[]).map((f) => {
      return f.runtimeId === runtime.id
        ? { ...f, header: { ...f.header, ...headerUpdate } }
        : f;
    });
  }

  /**
   * Get all frames registered to this capability
   * @description This is an observable that will update whenever applets are added or removed. See {@link BasicObservable} for more information.
   * @returns All applets registered to this capability, as an observable.
   * @example
   * ```ts
   * const frames = callFrameApplet.getFrames();
   * frames.onChange((frames) => {
   *  console.log('A frame was added or removed!', frames);
   * });
   * ```
   */
  getFrames(): BasicObservable<Applet[]> {
    return this._frames as BasicObservable<Applet[]>;
  }

  protected override getFrameItem(
    instance: Instance,
    runtime: IframeRuntime,
    config: AppletConfig,
  ): Applet {
    let header: Applet['header'] | undefined;

    if (config.header) {
      header = { ...config.header } as Applet['header'];
      header!.onClose = Boolean(config?.header?.onClose)
        ? () =>
            this.handleMessages(instance, config.header!.onClose as Message[])
        : undefined;
      header!.onBack = Boolean(config?.header?.onBack)
        ? () =>
            this.handleMessages(instance, config.header!.onBack as Message[])
        : undefined;
    }

    return {
      ...super.getFrameItem(instance, runtime, config),
      layout: config.layout ?? DEFAULT_LAYOUT,
      label: config.label,
      title: instance.manifest.title,

      onClose: Boolean(config.onClose)
        ? () => this.handleMessages(instance, config.onClose as Message[])
        : undefined,
      header,
    };
  }

  protected override getLoggerProps(
    instance: Instance,
    applet?: Applet,
  ): Record<string, unknown> {
    return {
      ...super.getLoggerProps(instance),
      applet: applet && { label: applet?.header?.title ?? applet.label },
    };
  }

  private enforceSingleFrame(instance: Instance, currentApplet: Applet): void {
    logger.warn(
      'Rejected attaching frame: only one frame may be active at a time',
      this.getLoggerProps(instance, currentApplet),
    );
    const currentInstance = this.runtimes[currentApplet.runtimeId]?.instance;
    if (!currentInstance) {
      logger.error(
        'Could not find extension instance for current applet',
        this.getLoggerProps(instance, currentApplet),
      );
      return;
    }
    const message = `${instance.manifest.title} cannot be launched while ${currentInstance.manifest.title} is running`;
    this.sendToastNotification(instance, message);
  }

  private sendToastNotification(instance: Instance, message: string): void {
    const capability = instance
      .getBridge()
      .getCapability<NotificationCapability>(Specs.Notification.id);
    if (!capability) {
      logger.error(
        'Error sending notification: Notification Capability not available',
        this.getLoggerProps(instance),
      );
      return;
    }

    capability.toast(
      { title: message, icon: NotificationIcon.Error },
      instance,
      true,
    );
  }
}
